import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import {
  TENANT_SEARCH_PROPERTY_KEYS,
  TENANT_SEARCH_OPERATORS,
  type SearchTenantsInput,
  type TenantSearchOperator,
  type TenantSearchPropertyKey,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { useAuth } from '@/auth/AuthProvider';
import { useTenantsSearch } from '@/features/tenants/useTenantsSearch';
import { TenantList } from '@/features/tenants/TenantList';
import { CreateCourtesyTenantDialog } from '@/features/tenants/CreateCourtesyTenantDialog';

const KNOWN_KEYS = new Set<string>(TENANT_SEARCH_PROPERTY_KEYS);
const KNOWN_OPERATORS = new Set<string>(TENANT_SEARCH_OPERATORS);
const DEFAULT_PAGE_SIZE = 25;

/**
 * Combinação não suportada pelo Firestore (callable responde
 * `invalid-argument` com `details.reason`). Os demais erros (rede, índice,
 * etc.) ganham mensagem genérica — não a redação específica de combo.
 */
function isUnsupportedComboError(error: unknown): boolean {
  const e = error as { code?: string; details?: { reason?: string } } | null;
  const reason = e?.details?.reason;
  return reason === 'unsupported_combo' || reason === 'unsupported_operator';
}

/**
 * Tela `/tenants` (rota inicial do admin) — lista paginada e filtrável de
 * todas as empresas, com "Criar tenant de cortesia".
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26 (Sprint 2, lista de
 * tenants com filtro, busca e listener real-time; hoje a busca é por
 * callable).
 *
 * Contrato: traduz os tokens do `PropertyFilter` 1:1 para
 * `SearchTenantsInput` (descarta texto livre, chave ou operador desconhecido
 * e valor vazio) e chama a callable `searchTenants` via `useTenantsSearch`.
 * Mudar o filtro volta para a página 1. Erro → toast: `unsupported_combo` /
 * `unsupported_operator` ganham a mensagem de combinação, o resto a genérica.
 * Criar cortesia (`CreateCourtesyTenantDialog`) usa o e-mail do admin logado
 * como OWNER e, no sucesso, navega para o detalhe.
 *
 * Usado por: rota `tenants` e índice `/` (`routes/router.tsx`), menu Empresas
 * (`AdminLayout`). Spec: `docs/screens/tenants-list-admin.md`; o modal de
 * criação tem spec própria, `docs/screens/admin-tenant-create.md`.
 *
 * Arquitetura: a busca é server-side (`apps/functions/src/admin/searchTenants.ts`)
 * e consulta o Firestore direto, sem read-model. Por isso token "standalone"
 * (texto, última transição, `status !=`) não combina com outro filtro: a
 * callable recusa com `invalid-argument` + `details.reason` em vez de deixar
 * a query cair em `failed-precondition` engolido.
 */
export function TenantsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status } = useAuth();
  const [query, setQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [creating, setCreating] = useState(false);

  // O tenant de cortesia nasce com o admin logado como OWNER — a rota já está
  // sob `RequireSuperAdmin`, então aqui sempre há usuário autorizado.
  const ownerEmail = status.kind === 'AUTHORIZED' ? (status.user.email ?? '') : '';

  // PropertyFilter tokens → tokens da callable (contrato 1:1). Descarta
  // tokens de free-text (sem propertyKey), operador não suportado ou vazios.
  const tokens = useMemo(
    () =>
      query.tokens
        .filter(
          (tk) =>
            typeof tk.propertyKey === 'string' &&
            KNOWN_KEYS.has(tk.propertyKey) &&
            KNOWN_OPERATORS.has(tk.operator) &&
            tk.value != null &&
            String(tk.value) !== '',
        )
        .map((tk) => ({
          propertyKey: tk.propertyKey as TenantSearchPropertyKey,
          operator: tk.operator as TenantSearchOperator,
          value: String(tk.value),
        })),
    [query],
  );

  const input: SearchTenantsInput = useMemo(
    () => ({ tokens, operation: 'and', page, pageSize }),
    [tokens, page, pageSize],
  );

  const state = useTenantsSearch(input, refreshNonce);

  const errorMessage =
    state.kind === 'ERROR'
      ? isUnsupportedComboError(state.error)
        ? t('tenants.filters.searchError')
        : t('tenants.filters.searchGenericError')
      : undefined;

  useEffect(() => {
    if (errorMessage) toast.error(errorMessage);
  }, [errorMessage]);

  return (
    <>
      <TenantList
        tenants={state.kind === 'READY' ? state.items : []}
        total={state.kind === 'READY' ? state.total : 0}
        loading={state.kind === 'LOADING'}
        errorMessage={errorMessage}
        query={query}
        onChangeQuery={(q): void => {
          setQuery(q);
          setPage(1);
        }}
        currentPageIndex={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        onRefresh={(): void => {
          setRefreshNonce((n) => n + 1);
        }}
        onCreateClick={(): void => {
          setCreating(true);
        }}
      />
      <CreateCourtesyTenantDialog
        open={creating}
        ownerEmail={ownerEmail}
        onClose={(): void => {
          setCreating(false);
        }}
        onCreated={(tenantId): void => {
          setCreating(false);
          setRefreshNonce((n) => n + 1);
          void navigate(`/tenants/${tenantId}`);
        }}
      />
    </>
  );
}
